import { useEditor, useNode } from "@craftjs/core";
import * as React from "react";
import scrollIntoView from "scroll-into-view-if-needed";
import type { Schedule, ScheduleItem } from "src/types";

import useDebouncedValue from "@/hooks/use-debounced-value";
import { useOnClickOutside } from "@/hooks/use-on-click-outside";

// Type for schedules after being processed by useScheduleFilter
export type FilteredSchedule = {
  items: ScheduleItem[];
  isNoFilters: boolean;
  date: string;
  count: number;
};
import useViewport from "@/hooks/use-viewport";
import getFilteredScheduleItems from "@/utils/get-filtered-schedule-items";
import { getUniqueScheduleParts } from "@/utils/get-unique-schedule-parts";
import { getUniqueScheduleTags } from "@/utils/get-unique-schedule-tags";
import togglePageScrollLock from "@/utils/toggle-page-scroll-lock";

export const useScheduleFilter = (schedules: Schedule[]) => {
  const {
    enabled,
    actions: { selectNode },
  } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const { node } = useNode((node) => ({ node }));

  const viewport = useViewport();
  const [searchValue, setSearchValue] = React.useState("");
  const [selectedTags, setSelectedTags] = React.useState([]);
  const [selectedParts, setSelectedParts] = React.useState([]);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  const isMobile = React.useMemo(() => viewport === "mobile", [viewport]);
  const debouncedValue = useDebouncedValue(searchValue);

  const handleTagSelection = (tagName: string) => {
    setSelectedTags((tags) =>
      tags.includes(tagName)
        ? tags.filter((tag) => tag !== tagName)
        : [...tags, tagName]
    );
  };

  const handlePartSelection = (partName: string) => {
    setSelectedParts((parts) =>
      parts.includes(partName)
        ? parts.filter((part) => part !== partName)
        : [...parts, partName]
    );
  };

  const clearAllSelections = React.useCallback(() => {
    setSelectedTags([]);
    setSelectedParts([]);
  }, []);

  const closeMenuAndEnableScroll = React.useCallback(() => {
    togglePageScrollLock({ method: "remove" });
    setIsMenuOpen(false);
  }, []);

  useOnClickOutside(menuRef, closeMenuAndEnableScroll, {
    isActive: isMenuOpen,
  });

  React.useEffect(() => {
    if (!isMenuOpen) return;

    const closeWhenFocusLeaves = (event: FocusEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        closeMenuAndEnableScroll();
      }
    };

    document.addEventListener("focusin", closeWhenFocusLeaves);
    return () => document.removeEventListener("focusin", closeWhenFocusLeaves);
  }, [isMenuOpen, closeMenuAndEnableScroll]);

  React.useEffect(closeMenuAndEnableScroll, [
    viewport,
    closeMenuAndEnableScroll,
  ]);
  React.useEffect(() => clearAllSelections(), [schedules, clearAllSelections]);

  const filters = React.useMemo(
    () => ({
      tagOptions: getUniqueScheduleTags(schedules),
      partOptions: getUniqueScheduleParts(schedules),
    }),
    [schedules]
  );

  const toggleMobileMenu = () => {
    if (isMenuOpen) {
      if (isMobile) {
        closeMenuAndEnableScroll();
        scrollIntoView(node.dom, { block: "start" });
      } else {
        setIsMenuOpen(false);
      }
    } else if (!isMenuOpen && enabled) {
      setIsMenuOpen(true);
      const node = document.getElementById("viewport-wrapper");
      if (node) {
        scrollIntoView(node, {
          block: "start",
          behavior: "auto",
        });
      }
      selectNode(null);
    } else if (!isMenuOpen && !enabled) {
      setIsMenuOpen(true);
      window.scroll({
        top: 0,
        left: 0,
        behavior: "auto",
      });
      togglePageScrollLock({ method: "add" });
    }
  };

  const filteredSchedules = React.useMemo(() => {
    const isNoFilters =
      !debouncedValue.trim() && !selectedParts.length && !selectedTags.length;

    return (schedules ?? []).map((scheduleByDate) => {
      const items = isNoFilters
        ? scheduleByDate.schedules
        : getFilteredScheduleItems({
            items: scheduleByDate.schedules,
            search: debouncedValue,
            selectedParts,
            selectedTags,
          });

      return {
        items,
        isNoFilters,
        date: scheduleByDate.date,
        count: items.length,
      };
    });
  }, [schedules, debouncedValue, selectedParts, selectedTags]);

  return {
    searchValue,
    setSearchValue,
    selectedTags,
    selectedParts,
    handleTagSelection,
    handlePartSelection,
    clearAllSelections,
    isMenuOpen,
    toggleMenu: isMobile ? toggleMobileMenu : () => setIsMenuOpen(!isMenuOpen),
    menuRef,
    filters,
    schedules: filteredSchedules,
  };
};
